import React, { useEffect, useState } from "react";
import { api, clp } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Plus, Trash2 } from "lucide-react";

const nuevoGrupo = (nombre = "", min = 0, max = null) => ({ nombre, min, max, opciones: [{ nombre: "", precio: 0 }] });
const PLANTILLAS = [["Quitar", 0, null], ["Agregar", 0, null], ["Punto de la carne", 1, 1]];

/** Editor de opciones del producto (grupos con mínimo/máximo y precio por opción). */
export default function ModificadoresDialog({ product, onClose, onSaved }) {
  const [grupos, setGrupos] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setGrupos(JSON.parse(JSON.stringify(product?.modificadores || []))); }, [product]);
  if (!product) return null;
  const upd = (gi, patch) => setGrupos(grupos.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const updOp = (gi, oi, patch) => upd(gi, { opciones: grupos[gi].opciones.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });
  const guardar = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/products/${product.product_id}/modificadores`,
        grupos.map((g) => ({ ...g, min: Number(g.min) || 0, max: g.max ? Number(g.max) : null, opciones: g.opciones.filter((o) => o.nombre.trim()) })));
      toast.success("Opciones guardadas"); onSaved?.(data.modificadores); onClose();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Revisa los grupos: cada uno necesita nombre y opciones"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mods-dialog">
        <DialogHeader><DialogTitle>Opciones de {product.nombre}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Al vender este producto en el POS o el ecommerce se preguntan estas opciones. Las elegidas salen en el KDS y en el ticket; las con precio se suman al valor.</p>
        <div className="space-y-3">
          {grupos.map((g, gi) => (
            <div key={gi} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`mods-grupo-${gi}`}>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="flex-1 min-w-[160px] text-[11px] font-semibold text-slate-600">Grupo
                  <Input value={g.nombre} onChange={(e) => upd(gi, { nombre: e.target.value })} placeholder="Ej: Agregar" className="h-9 rounded-lg mt-0.5" data-testid={`mods-grupo-nombre-${gi}`} /></label>
                <label className="text-[11px] font-semibold text-slate-600">Mínimo
                  <Input type="number" min={0} value={g.min} onChange={(e) => upd(gi, { min: e.target.value })} className="h-9 w-20 rounded-lg mt-0.5" data-testid={`mods-grupo-min-${gi}`} /></label>
                <label className="text-[11px] font-semibold text-slate-600">Máximo
                  <Input type="number" min={0} value={g.max ?? ""} placeholder="Sin límite" onChange={(e) => upd(gi, { max: e.target.value })} className="h-9 w-24 rounded-lg mt-0.5" data-testid={`mods-grupo-max-${gi}`} /></label>
                <Button size="icon" variant="ghost" onClick={() => setGrupos(grupos.filter((_, i) => i !== gi))} className="text-rose-600" data-testid={`mods-grupo-del-${gi}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              {g.opciones.map((o, oi) => (
                <div key={oi} className="flex gap-2 items-center">
                  <Input value={o.nombre} onChange={(e) => updOp(gi, oi, { nombre: e.target.value })} placeholder="Ej: Sin tomate / Mayonesa" className="h-9 rounded-lg flex-1" data-testid={`mods-op-nombre-${gi}-${oi}`} />
                  <Input type="number" min={0} value={o.precio} onChange={(e) => updOp(gi, oi, { precio: e.target.value })} className="h-9 w-28 rounded-lg text-right" title="Precio extra (0 = gratis)" data-testid={`mods-op-precio-${gi}-${oi}`} />
                  <Button size="icon" variant="ghost" onClick={() => upd(gi, { opciones: g.opciones.filter((_, i) => i !== oi) })} className="text-slate-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => upd(gi, { opciones: [...g.opciones, { nombre: "", precio: 0 }] })} data-testid={`mods-op-add-${gi}`}><Plus className="w-3 h-3 mr-1" /> Opción</Button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {PLANTILLAS.map(([n, mi, mx]) => (
            <Button key={n} size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setGrupos([...grupos, nuevoGrupo(n, mi, mx)])} data-testid={`mods-add-${n}`}><Plus className="w-3 h-3 mr-1" /> {n}</Button>
          ))}
          <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setGrupos([...grupos, nuevoGrupo()])} data-testid="mods-add-grupo"><Plus className="w-3 h-3 mr-1" /> Otro grupo</Button>
        </div>
        <p className="text-[11px] text-slate-500">Mínimo 1 y máximo 1 = obligatorio, una sola opción. Precio 0 = sin costo{grupos.length ? "" : " · sin grupos, el producto se vende directo"}.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancelar</Button>
          <Button disabled={busy} onClick={guardar} className="rounded-xl bg-sky-500 hover:bg-sky-600 text-white" data-testid="mods-save">{busy ? "Guardando…" : `Guardar opciones${grupos.length ? "" : " (ninguna)"}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Selector en la venta: devuelve {sel:[{grupo_id, opcion_id}], txt:[{grupo,nombre,precio}], extra}. */
export function ElegirModificadores({ product, onCancel, onConfirm }) {
  const [sel, setSel] = useState({});
  if (!product) return null;
  const toggle = (g, o) => {
    const cur = sel[g.grupo_id] || [];
    let next;
    if (g.max === 1) next = cur.includes(o.opcion_id) ? (g.min ? cur : []) : [o.opcion_id];
    else if (cur.includes(o.opcion_id)) next = cur.filter((x) => x !== o.opcion_id);
    else if (g.max && cur.length >= g.max) { toast.error(`Máximo ${g.max} en «${g.nombre}»`); return; }
    else next = [...cur, o.opcion_id];
    setSel({ ...sel, [g.grupo_id]: next });
  };
  const faltan = (product.modificadores || []).filter((g) => (sel[g.grupo_id] || []).length < (g.min || 0));
  const elegidos = (product.modificadores || []).flatMap((g) => (sel[g.grupo_id] || []).map((id) => ({ g, o: g.opciones.find((x) => x.opcion_id === id) })));
  const extra = elegidos.reduce((a, x) => a + Number(x.o.precio || 0), 0);
  const confirmar = () => onConfirm({
    sel: elegidos.map(({ g, o }) => ({ grupo_id: g.grupo_id, opcion_id: o.opcion_id })),
    txt: elegidos.map(({ g, o }) => ({ grupo: g.nombre, nombre: o.nombre, precio: Number(o.precio || 0) })), extra,
  });
  const base = Number(product.precio || 0);
  const reglas = (g) => (g.min ? (g.max === 1 ? "Obligatorio · elige 1" : `Obligatorio · mínimo ${g.min}`) : g.max ? `Opcional · hasta ${g.max}` : "Opcional");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="w-[calc(100%-1.5rem)] sm:w-full rounded-3xl max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="mods-elegir">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 text-left space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">Personaliza tu pedido</p>
          <DialogTitle className="text-xl font-bold text-slate-900 font-display leading-tight pr-6">{product.nombre}</DialogTitle>
          {base > 0 && <p className="text-sm text-slate-500 font-mono-num">{clp(base)}</p>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {(product.modificadores || []).map((g) => {
            const n = (sel[g.grupo_id] || []).length;
            const falta = n < (g.min || 0);
            return (
              <section key={g.grupo_id} className="space-y-2.5" data-testid={`mods-elegir-grupo-${g.grupo_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-900">{g.nombre}</h3>
                  <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full transition-colors ${falta ? "bg-amber-50 text-amber-700" : g.min ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{reglas(g)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {g.opciones.map((o) => {
                    const on = (sel[g.grupo_id] || []).includes(o.opcion_id);
                    const precio = Number(o.precio || 0);
                    return (
                      <button key={o.opcion_id} type="button" onClick={() => toggle(g, o)} data-testid={`mods-elegir-${o.opcion_id}`} aria-pressed={on}
                              className={`group h-14 px-3.5 rounded-2xl border text-left flex items-center gap-3 transition-[background-color,border-color,box-shadow,transform] duration-150 active:scale-[0.98] ${on ? "border-sky-500 bg-sky-50 shadow-[0_0_0_1px_rgb(14,165,233)]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                        <span className={`w-5 h-5 shrink-0 ${g.max === 1 ? "rounded-full" : "rounded-md"} border-2 grid place-items-center transition-colors ${on ? "border-sky-500 bg-sky-500" : "border-slate-300 bg-white"}`}>
                          {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </span>
                        <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 leading-tight line-clamp-2">{o.nombre}</span>
                        <span className={`shrink-0 text-xs font-mono-num ${precio > 0 ? "font-semibold text-slate-700" : "text-slate-400"}`}>{precio > 0 ? `+${clp(precio)}` : "—"}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/70 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Opciones <strong className="font-mono-num font-semibold text-slate-700" data-testid="mods-elegir-extra">{extra > 0 ? `+${clp(extra)}` : clp(0)}</strong></span>
            {base > 0 && <span className="text-slate-500">Total <strong className="font-mono-num text-base font-bold text-slate-900" data-testid="mods-elegir-total">{clp(base + extra)}</strong></span>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={onCancel} className="h-11 rounded-xl border-slate-200" data-testid="mods-elegir-cancel">Cancelar</Button>
            <Button disabled={faltan.length > 0} onClick={confirmar} className="h-11 rounded-xl bg-sky-600 hover:bg-sky-700 text-white disabled:bg-slate-200 disabled:text-slate-500 disabled:opacity-100" data-testid="mods-elegir-ok">
              {faltan.length ? `Elige ${faltan[0].nombre.toLowerCase()}` : "Agregar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
